import { Match, Result, Schema } from 'effect';

import { dateValueSchema } from './queryDates';
import { type Token, tokenize, unquote } from './queryLexer';
import {
  type QueryField,
  type QueryOperator,
  type QuerySchema,
  booleanFieldSchema,
  choiceFieldSchema,
  dateFieldSchema,
  equalityOperatorSchema,
  fieldsMatching,
  findField,
  idFieldSchema,
  membershipOperatorSchema,
  numberFieldSchema,
  operatorsFor,
  orderingOperatorSchema,
  presenceOperatorSchema,
  queryFieldSchema,
  queryOperatorSchema,
  textFieldSchema,
  textOperatorSchema,
} from './querySchema';

const textValueSchema = Schema.Struct({ kind: Schema.Literal('string'), text: Schema.String });
const numberValueSchema = Schema.Struct({ kind: Schema.Literal('number'), value: Schema.Finite });
const booleanValueSchema = Schema.Struct({
  kind: Schema.Literal('boolean'),
  value: Schema.Boolean,
});
const dateQueryValueSchema = Schema.Struct({ kind: Schema.Literal('date'), text: dateValueSchema });
const emptyValueSchema = Schema.Struct({ kind: Schema.Literal('empty') });
const listOf = <S extends Schema.Top>(item: S) =>
  Schema.Struct({
    kind: Schema.Literal('list'),
    items: Schema.NonEmptyArray(item),
  });
export const queryScalarSchema = Schema.Union([
  textValueSchema,
  numberValueSchema,
  booleanValueSchema,
  dateQueryValueSchema,
]);
export type QueryScalar = typeof queryScalarSchema.Type;
export const queryValueSchema = Schema.Union([
  queryScalarSchema,
  listOf(textValueSchema),
  listOf(numberValueSchema),
  emptyValueSchema,
]);
export type QueryValue = typeof queryValueSchema.Type;

const clause = <F extends Schema.Top, O extends Schema.Top, V extends Schema.Top>(
  field: F,
  op: O,
  value: V,
) => Schema.Struct({ type: Schema.Literal('clause'), field, op, value });
const numericFieldSchema = Schema.Union([idFieldSchema, numberFieldSchema]);
const nullableFieldSchema = Schema.Union([
  numberFieldSchema,
  textFieldSchema,
  choiceFieldSchema,
  dateFieldSchema,
]);
const clauseShape = Schema.Union([
  clause(numericFieldSchema, orderingOperatorSchema, numberValueSchema),
  clause(numericFieldSchema, membershipOperatorSchema, listOf(numberValueSchema)),
  clause(textFieldSchema, textOperatorSchema, textValueSchema),
  clause(choiceFieldSchema, equalityOperatorSchema, textValueSchema),
  clause(
    Schema.Union([textFieldSchema, choiceFieldSchema]),
    membershipOperatorSchema,
    listOf(textValueSchema),
  ),
  clause(booleanFieldSchema, Schema.Literal('='), booleanValueSchema),
  clause(
    dateFieldSchema,
    queryOperatorSchema.pick(['=', '>', '>=', '<', '<=']),
    dateQueryValueSchema,
  ),
  clause(nullableFieldSchema, presenceOperatorSchema, emptyValueSchema),
]);

const valueAllowed = (field: QueryField, value: QueryValue): boolean => {
  if (value.kind === 'empty') return true;
  if (value.kind === 'list') return value.items.every((item) => valueAllowed(field, item));
  if (field.type === 'choice' && value.kind === 'string') return field.values.includes(value.text);
  if ((field.type === 'id' || field.type === 'number') && value.kind === 'number') {
    return Schema.is(field.valueSchema ?? Schema.Finite)(value.value);
  }
  return true;
};

export const queryClauseSchema = clauseShape
  .check(
    Schema.makeFilter((condition) => operatorsFor(condition.field).includes(condition.op), {
      message: 'The field does not support this operator',
    }),
    Schema.makeFilter((condition) => valueAllowed(condition.field, condition.value), {
      message: 'The value does not belong to this field',
    }),
  )
  .pipe(Schema.brand('QueryClause'));
export type QueryClause = typeof queryClauseSchema.Type;

export const queryTextSearchSchema = Schema.Struct({
  type: Schema.Literal('search'),
  text: Schema.String.check(
    Schema.makeFilter((text) => text.trim() !== '', { message: 'Enter search text' }),
  ),
});

export type QueryExpression =
  | QueryClause
  | typeof queryTextSearchSchema.Type
  | { readonly type: 'and'; readonly left: QueryExpression; readonly right: QueryExpression }
  | { readonly type: 'or'; readonly left: QueryExpression; readonly right: QueryExpression }
  | { readonly type: 'not'; readonly expr: QueryExpression };

export const queryExpressionSchema: Schema.Codec<QueryExpression, unknown> = Schema.Union([
  queryClauseSchema,
  queryTextSearchSchema,
  Schema.Struct({
    type: Schema.Literal('and'),
    left: Schema.suspend(() => queryExpressionSchema),
    right: Schema.suspend(() => queryExpressionSchema),
  }),
  Schema.Struct({
    type: Schema.Literal('or'),
    left: Schema.suspend(() => queryExpressionSchema),
    right: Schema.suspend(() => queryExpressionSchema),
  }),
  Schema.Struct({ type: Schema.Literal('not'), expr: Schema.suspend(() => queryExpressionSchema) }),
]);

export const spanKindSchema = Schema.Literals([
  'field',
  'operator',
  'keyword',
  'string',
  'number',
  'date',
  'enum',
  'boolean',
  'paren',
  'error',
  'text',
]);
export type SpanKind = typeof spanKindSchema.Type;
const sourcePositionSchema = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const sourceRange = { start: sourcePositionSchema, end: sourcePositionSchema };
export const spanSchema = Schema.Struct({ kind: spanKindSchema, ...sourceRange });
export type Span = typeof spanSchema.Type;
export const queryErrorSchema = Schema.Struct({ ...sourceRange, message: Schema.NonEmptyString });
export type QueryError = typeof queryErrorSchema.Type;
const completionPrefix = { prefix: Schema.String, start: sourcePositionSchema };
export const expectingSchema = Schema.Union([
  Schema.Struct({ what: Schema.Literal('field'), ...completionPrefix }),
  Schema.Struct({ what: Schema.Literal('operator'), field: queryFieldSchema, ...completionPrefix }),
  Schema.Struct({
    what: Schema.Literal('value'),
    field: queryFieldSchema,
    op: queryOperatorSchema,
    ...completionPrefix,
    inList: Schema.Boolean,
  }),
  Schema.Struct({ what: Schema.Literal('keyword'), ...completionPrefix }),
  Schema.Struct({ what: Schema.Literal('nothing') }),
]);
export type Expecting = typeof expectingSchema.Type;
const queryFeedback = { spans: Schema.Array(spanSchema), expecting: expectingSchema };
export const parseResultSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal('text'), text: Schema.String, expecting: expectingSchema }),
  Schema.Struct({ kind: Schema.Literal('incomplete'), ...queryFeedback }),
  Schema.Struct({
    kind: Schema.Literal('invalid'),
    ...queryFeedback,
    errors: Schema.NonEmptyArray(queryErrorSchema),
  }),
  Schema.Struct({
    kind: Schema.Literal('valid'),
    ...queryFeedback,
    expression: queryExpressionSchema,
  }),
]);
export type ParseResult = typeof parseResultSchema.Type;

const keywords = ['AND', 'OR', 'NOT', 'IN', 'IS', 'EMPTY'] as const;

const isKeyword = (token: Token | undefined, word: string): boolean =>
  token?.kind === 'word' && token.text.toUpperCase() === word;

interface State {
  readonly tokens: readonly Token[];
  readonly index: number;
  readonly spans: readonly Span[];
  readonly errors: readonly QueryError[];
  readonly expecting: Expecting;
}

interface Step<A> {
  readonly state: State;
  readonly value: A;
}

const at = (state: State): Token | undefined => state.tokens[state.index];

const skipSpace = (state: State): State =>
  at(state)?.kind === 'space' ? skipSpace({ ...state, index: state.index + 1 }) : state;

const advance = (state: State, span?: Span): State => ({
  ...state,
  index: state.index + 1,
  spans: span === undefined ? state.spans : [...state.spans, span],
});

const withError = (
  state: State,
  token: Token | undefined,
  message: string,
  input: string,
): State => ({
  ...state,
  spans:
    token === undefined
      ? state.spans
      : [...state.spans, { kind: 'error', start: token.start, end: token.end }],
  errors: [
    ...state.errors,
    token === undefined
      ? { start: input.length, end: input.length, message }
      : { start: token.start, end: token.end, message },
  ],
});

const expecting = (state: State, value: Expecting): State => ({ ...state, expecting: value });

const valueKindFor = (field: QueryField, token: Token): SpanKind =>
  Match.value(field).pipe(
    Match.when({ type: 'date' }, () => 'date' as const),
    Match.when({ type: 'choice' }, () => 'enum' as const),
    Match.when({ type: 'boolean' }, () => 'boolean' as const),
    Match.orElse(() =>
      token.kind === 'number' || token.kind === 'relative' ? 'number' : 'string',
    ),
  );

const numberValue = (
  field: Extract<QueryField, { type: 'number' | 'id' }>,
  raw: string,
): Result.Result<QueryScalar, string> => {
  const parsed = Schema.decodeUnknownResult(Schema.Finite)(Number(raw)).pipe(
    Result.flatMap(Schema.decodeUnknownResult(field.valueSchema ?? Schema.Finite)),
    Result.flatMap(Schema.decodeUnknownResult(Schema.Finite)),
  );
  return Result.mapBoth(parsed, {
    onSuccess: (value): QueryScalar => ({ kind: 'number', value }),
    onFailure: (error) => `${field.label}: ${error.message}`,
  });
};

const booleanValue = (field: QueryField, raw: string): Result.Result<QueryScalar, string> =>
  Match.value(raw.toLowerCase()).pipe(
    Match.when(Match.is('true', 'yes'), () =>
      Result.succeed({ kind: 'boolean' as const, value: true }),
    ),
    Match.when(Match.is('false', 'no'), () =>
      Result.succeed({ kind: 'boolean' as const, value: false }),
    ),
    Match.orElse(() => Result.fail(`${field.label} is true or false`)),
  );

const choiceValue = (
  field: Extract<QueryField, { type: 'choice' }>,
  raw: string,
): Result.Result<QueryScalar, string> => {
  const choice = field.values.find((value) => value.toLowerCase() === raw.toLowerCase());
  if (choice === undefined)
    return Result.fail(`${field.label} is one of ${field.values.join(', ')}`);
  return Result.succeed({ kind: 'string', text: choice });
};

const dateValue = (raw: string): Result.Result<QueryScalar, string> =>
  Schema.decodeUnknownResult(dateValueSchema)(raw).pipe(
    Result.mapBoth({
      onSuccess: (text): QueryScalar => ({ kind: 'date', text }),
      onFailure: () =>
        'Not a date. Try today, yesterday, -7d, "last 30 days", "this month" or 2026-01-31',
    }),
  );

const scalarValue = (field: QueryField, token: Token): Result.Result<QueryScalar, string> => {
  const raw = unquote(token.text);
  return Match.value(field).pipe(
    Match.when({ type: 'id' }, (numeric) => numberValue(numeric, raw)),
    Match.when({ type: 'number' }, (numeric) => numberValue(numeric, raw)),
    Match.when({ type: 'boolean' }, () => booleanValue(field, raw)),
    Match.when({ type: 'date' }, () => dateValue(raw)),
    Match.when({ type: 'choice' }, (choice) => choiceValue(choice, raw)),
    Match.when({ type: 'text' }, () => Result.succeed({ kind: 'string' as const, text: raw })),
    Match.exhaustive,
  );
};

const parseScalar = (
  state: State,
  field: QueryField,
  op: QueryOperator,
  inList: boolean,
  input: string,
): Step<QueryScalar | null> => {
  const s = skipSpace(state);
  const token = at(s);
  const start = token?.start ?? input.length;
  if (token === undefined || token.kind === 'rparen' || token.kind === 'comma') {
    return {
      state: expecting(s, { what: 'value', field, op, prefix: '', start, inList }),
      value: null,
    };
  }
  if (token.kind === 'lparen' || token.kind === 'op') {
    return {
      state: withError(advance(s), token, `Expected a value for ${field.label}`, input),
      value: null,
    };
  }
  if (token.kind === 'string' && token.quote === 'open') {
    return {
      state: expecting(
        advance(s, { kind: valueKindFor(field, token), start: token.start, end: token.end }),
        {
          what: 'value',
          field,
          op,
          prefix: unquote(token.text),
          start,
          inList,
        },
      ),
      value: null,
    };
  }
  const parsed = scalarValue(field, token);
  if (Result.isFailure(parsed)) {
    const partial = token.end === input.length;
    return {
      state: partial
        ? expecting(
            advance(s, { kind: valueKindFor(field, token), start: token.start, end: token.end }),
            {
              what: 'value',
              field,
              op,
              prefix: unquote(token.text),
              start: token.start,
              inList,
            },
          )
        : withError(advance(s), token, parsed.failure, input),
      value: null,
    };
  }
  return {
    state: expecting(
      advance(s, { kind: valueKindFor(field, token), start: token.start, end: token.end }),
      {
        what: 'nothing',
      },
    ),
    value: parsed.success,
  };
};

const parseList = (
  state: State,
  field: QueryField,
  op: QueryOperator,
  input: string,
): Step<QueryValue | null> => {
  const open = skipSpace(state);
  const paren = at(open);
  if (paren?.kind !== 'lparen') {
    return {
      state: expecting(open, {
        what: 'value',
        field,
        op,
        prefix: '',
        start: paren?.start ?? input.length,
        inList: true,
      }),
      value: null,
    };
  }
  const loop = (s: State, items: readonly QueryScalar[]): Step<QueryValue | null> => {
    const item = parseScalar(s, field, op, true, input);
    const collected = item.value === null ? items : [...items, item.value];
    const next = skipSpace(item.state);
    const token = at(next);
    if (token?.kind === 'comma') {
      return loop(
        item.value === null
          ? withError(advance(next), token, `Expected a value for ${field.label}`, input)
          : advance(next),
        collected,
      );
    }
    if (token?.kind === 'rparen') {
      if (item.value === null) {
        return {
          state: withError(advance(next), token, `Expected a value for ${field.label}`, input),
          value: null,
        };
      }
      return {
        state: expecting(advance(next, { kind: 'paren', start: token.start, end: token.end }), {
          what: 'nothing',
        }),
        value: Schema.decodeUnknownSync(queryValueSchema)({ kind: 'list', items: collected }),
      };
    }
    if (token === undefined) return { state: next, value: null };
    return {
      state: withError(advance(next), token, 'Expected , or ) in the list', input),
      value: null,
    };
  };
  return loop(advance(open, { kind: 'paren', start: paren.start, end: paren.end }), []);
};

const keywordOperators: readonly {
  readonly words: readonly string[];
  readonly op: QueryOperator;
}[] = [
  { words: ['IN'], op: 'IN' },
  { words: ['NOT', 'IN'], op: 'NOT IN' },
  { words: ['IS', 'EMPTY'], op: 'IS EMPTY' },
  { words: ['IS', 'NOT', 'EMPTY'], op: 'IS NOT EMPTY' },
];

const wordRun = (state: State, max: number): readonly Token[] => {
  const collect = (s: State, acc: readonly Token[]): readonly Token[] => {
    const token = at(s);
    return acc.length >= max || token?.kind !== 'word'
      ? acc
      : collect(skipSpace(advance(s)), [...acc, token]);
  };
  return collect(state, []);
};

const matchesPrefix = (words: readonly string[], tokens: readonly Token[]): boolean =>
  tokens.every((token, index) => words[index]?.toUpperCase() === token.text.toUpperCase());

const markOperator = (
  state: State,
  field: QueryField,
  op: QueryOperator,
  tokens: readonly Token[],
  input: string,
): Step<QueryOperator | null> => {
  const consumed = tokens.reduce((s, _token) => skipSpace(advance(s)), state);
  const spans = tokens.map((t) => ({ kind: 'operator' as const, start: t.start, end: t.end }));
  return operatorsFor(field).includes(op)
    ? { state: { ...consumed, spans: [...consumed.spans, ...spans] }, value: op }
    : {
        state: withError(consumed, tokens[0], `${field.label} does not support ${op}`, input),
        value: null,
      };
};

const keywordOperator = (
  state: State,
  field: QueryField,
  input: string,
): Step<QueryOperator | null> => {
  const run = wordRun(state, 3);
  const exact = keywordOperators.find(
    (candidate) =>
      candidate.words.length <= run.length &&
      matchesPrefix(candidate.words, run.slice(0, candidate.words.length)),
  );
  if (exact !== undefined)
    return markOperator(state, field, exact.op, run.slice(0, exact.words.length), input);
  const partial = keywordOperators.some((candidate) => matchesPrefix(candidate.words, run));
  const last = run.at(-1);
  if (partial && last !== undefined && last.end === input.length) {
    const first = run[0];
    return {
      state: expecting(state, {
        what: 'operator',
        field,
        prefix: run.map((t) => t.text).join(' '),
        start: first.start,
      }),
      value: null,
    };
  }
  return {
    state: withError(advance(state), run[0], `Expected an operator after ${field.label}`, input),
    value: null,
  };
};

const parseOperator = (
  state: State,
  field: QueryField,
  input: string,
): Step<QueryOperator | null> => {
  const s = skipSpace(state);
  const token = at(s);
  if (token === undefined) {
    return {
      state: expecting(s, { what: 'operator', field, prefix: '', start: input.length }),
      value: null,
    };
  }
  if (token.kind === 'op')
    return markOperator(
      s,
      field,
      Schema.decodeUnknownSync(queryOperatorSchema)(token.text),
      [token],
      input,
    );
  if (token.kind === 'word') return keywordOperator(s, field, input);
  return {
    state: withError(advance(s), token, `Expected an operator after ${field.label}`, input),
    value: null,
  };
};

const parseClause = (
  state: State,
  schema: QuerySchema,
  input: string,
): Step<QueryExpression | null> => {
  const s = skipSpace(state);
  const token = at(s);
  if (token === undefined) {
    return { state: expecting(s, { what: 'field', prefix: '', start: input.length }), value: null };
  }
  if (token.kind !== 'word') {
    return { state: withError(advance(s), token, 'Expected a field name', input), value: null };
  }
  const field = findField(schema, token.text);
  if (field === undefined) {
    const partial = token.end === input.length;
    return {
      state: partial
        ? expecting(advance(s, { kind: 'text', start: token.start, end: token.end }), {
            what: 'field',
            prefix: token.text,
            start: token.start,
          })
        : withError(advance(s), token, `Unknown field ${token.text}`, input),
      value: null,
    };
  }
  const afterField = advance(s, { kind: 'field', start: token.start, end: token.end });
  const op = parseOperator(afterField, field, input);
  if (op.value === null) return { state: op.state, value: null };
  if (op.value === 'IS EMPTY' || op.value === 'IS NOT EMPTY') {
    return {
      state: expecting(op.state, { what: 'nothing' }),
      value: Schema.decodeUnknownSync(queryClauseSchema)({
        type: 'clause',
        field,
        op: op.value,
        value: { kind: 'empty' },
      }),
    };
  }
  const value =
    op.value === 'IN' || op.value === 'NOT IN'
      ? parseList(op.state, field, op.value, input)
      : parseScalar(op.state, field, op.value, false, input);
  return {
    state: value.state,
    value:
      value.value === null
        ? null
        : Schema.decodeUnknownSync(queryClauseSchema)({
            type: 'clause',
            field,
            op: op.value,
            value: value.value,
          }),
  };
};

const parseTextSearch = (
  s: State,
  token: Extract<Token, { kind: 'string' }>,
  input: string,
): Step<QueryExpression | null> => {
  const marked = advance(s, { kind: 'string', start: token.start, end: token.end });
  if (token.quote === 'open') return { state: marked, value: null };
  const text = unquote(token.text);
  if (text.trim() === '')
    return { state: withError(marked, token, 'Enter search text', input), value: null };
  return { state: expecting(marked, { what: 'nothing' }), value: { type: 'search', text } };
};

const parsePrimary = (
  state: State,
  schema: QuerySchema,
  input: string,
): Step<QueryExpression | null> => {
  const s = skipSpace(state);
  const token = at(s);
  if (token?.kind === 'string') return parseTextSearch(s, token, input);
  if (isKeyword(token, 'NOT') && token) {
    const inner = parsePrimary(
      advance(s, { kind: 'keyword', start: token.start, end: token.end }),
      schema,
      input,
    );
    return {
      state: inner.state,
      value: inner.value === null ? null : { type: 'not', expr: inner.value },
    };
  }
  if (token?.kind === 'lparen') {
    const inner = parseOr(
      advance(s, { kind: 'paren', start: token.start, end: token.end }),
      schema,
      input,
    );
    const closing = skipSpace(inner.state);
    const close = at(closing);
    if (close?.kind === 'rparen') {
      return {
        state: advance(closing, { kind: 'paren', start: close.start, end: close.end }),
        value: inner.value,
      };
    }
    return {
      state: close === undefined ? closing : withError(closing, close, 'Expected )', input),
      value: null,
    };
  }
  return parseClause(s, schema, input);
};

const combineQueries = (
  keyword: 'AND' | 'OR',
  left: QueryExpression | null,
  right: QueryExpression | null,
): QueryExpression | null => {
  if (left === null || right === null) return null;
  return Match.value(keyword).pipe(
    Match.when('AND', (): QueryExpression => ({ type: 'and', left, right })),
    Match.when('OR', (): QueryExpression => ({ type: 'or', left, right })),
    Match.exhaustive,
  );
};

const parseBinary = (
  state: State,
  keyword: 'AND' | 'OR',
  below: (s: State) => Step<QueryExpression | null>,
): Step<QueryExpression | null> => {
  const first = below(state);
  const loop = (s: State, left: QueryExpression | null): Step<QueryExpression | null> => {
    const next = skipSpace(s);
    const token = at(next);
    if (!isKeyword(token, keyword) || token === undefined) return { state: s, value: left };
    const right = below(advance(next, { kind: 'keyword', start: token.start, end: token.end }));
    const combined = combineQueries(keyword, left, right.value);
    return loop(right.state, combined);
  };
  return loop(first.state, first.value);
};

const parseAnd = (state: State, schema: QuerySchema, input: string): Step<QueryExpression | null> =>
  parseBinary(state, 'AND', (s) => parsePrimary(s, schema, input));

const parseOr = (state: State, schema: QuerySchema, input: string): Step<QueryExpression | null> =>
  parseBinary(state, 'OR', (s) => parseAnd(s, schema, input));

const startsClause = (second: Token | undefined): boolean =>
  second !== undefined &&
  (second.kind === 'op' ||
    isKeyword(second, 'IN') ||
    isKeyword(second, 'IS') ||
    isKeyword(second, 'NOT'));

const looksLikeQuery = (tokens: readonly Token[], schema: QuerySchema): boolean => {
  const meaningful = tokens.filter((token) => token.kind !== 'space');
  const [first, second] = meaningful;
  if (first === undefined) return false;
  if (first.kind === 'string') return true;
  if (first.kind === 'lparen' || isKeyword(first, 'NOT')) return true;
  if (first.kind !== 'word') return false;
  if (startsClause(second)) return true;
  return second === undefined && findField(schema, first.text) !== undefined;
};

const fieldPrefixOnly = (
  tokens: readonly Token[],
  schema: QuerySchema,
  input: string,
): Expecting => {
  const meaningful = tokens.filter((token) => token.kind !== 'space');
  const [first] = meaningful;
  if (
    meaningful.length === 1 &&
    first.kind === 'word' &&
    first.end === input.length &&
    fieldsMatching(schema, first.text).length > 0
  ) {
    return { what: 'field', prefix: first.text, start: first.start };
  }
  return input.trim() === ''
    ? { what: 'field', prefix: '', start: input.length }
    : { what: 'nothing' };
};

export const parseQuery = (input: string, schema: QuerySchema): ParseResult => {
  const tokens = tokenize(input);
  if (!looksLikeQuery(tokens, schema)) {
    return { kind: 'text', text: input.trim(), expecting: fieldPrefixOnly(tokens, schema, input) };
  }
  const initial: State = {
    tokens,
    index: 0,
    spans: [],
    errors: [],
    expecting: { what: 'nothing' },
  };
  const result = parseOr(initial, schema, input);
  return finishQuery(result, input);
};

const finishClause = (result: Step<QueryExpression | null>, input: string): State => {
  const rest = skipSpace(result.state);
  const trailing = at(rest);
  if (trailing === undefined) {
    if (result.value !== null && result.state.expecting.what === 'nothing') {
      return expecting(rest, { what: 'keyword', prefix: '', start: input.length });
    }
    return rest;
  }
  const partialKeyword =
    trailing.kind === 'word' &&
    trailing.end === input.length &&
    ['AND', 'OR'].some((keyword) => keyword.startsWith(trailing.text.toUpperCase()));
  if (partialKeyword && result.value !== null) {
    return expecting(advance(rest, { kind: 'text', start: trailing.start, end: trailing.end }), {
      what: 'keyword',
      prefix: trailing.text,
      start: trailing.start,
    });
  }
  return withError(advance(rest), trailing, `Unexpected ${trailing.text}`, input);
};

const readyToExecute = (next: Expecting): boolean =>
  Match.value(next).pipe(
    Match.when({ what: 'nothing' }, () => true),
    Match.when({ what: 'keyword', prefix: '' }, () => true),
    Match.orElse(() => false),
  );

const finishQuery = (result: Step<QueryExpression | null>, input: string): ParseResult => {
  const state = finishClause(result, input);
  const feedback = { spans: state.spans, expecting: state.expecting };
  const [firstError, ...otherErrors] = state.errors;
  if (firstError !== undefined)
    return { kind: 'invalid', ...feedback, errors: [firstError, ...otherErrors] };
  if (result.value === null || !readyToExecute(state.expecting))
    return { kind: 'incomplete', ...feedback };
  return { kind: 'valid', ...feedback, expression: result.value };
};

export const keywordList = keywords;
